//
//  AmwellExperience.Web
//  MyHealth+
//
//  Created by jinyeob kim on 1/23/20.
//  Copyright © 2020 Facebook. All rights reserved.
//

import awsdk from 'awsdk';
import * as types from './types/index';
import StorageService from './services/StorageService';
import { getData } from '../normalizer';
import {
  Consumer,
  Authentication,
  Practice,
  ProviderDetails,
  Provider,
  FirstAvailableConfiguration,
  VisitContext,
  Visit,
  VisitSummary,
  Appointment
} from '../sdk/types';
import { sessionKeys } from './constants';

export default class AmwellFactory {
  storageService: StorageService;

  constructor() {
    this.storageService = new StorageService();
  }

  restoreConsumer(consumer?: Consumer): Consumer | undefined {
    if (!consumer) return undefined;
    return awsdk.AWSDKFactory.restoreConsumer(JSON.stringify(consumer));
  }

  restoreAuthentication(authentication?: Authentication): Authentication | undefined {
    if (!authentication) return undefined;
    return awsdk.AWSDKFactory.restoreAuthentication(JSON.stringify(authentication));
  }

  restorePractice(practice?: Practice): Practice | undefined {
    if (!practice) return undefined;
    return awsdk.AWSDKFactory.restorePractice(JSON.stringify(practice));
  }

  restoreProvider(provider?: ProviderDetails | Provider): ProviderDetails | undefined {
    if (!provider) return undefined;
    return awsdk.AWSDKFactory.restoreProviderDetails(JSON.stringify(provider));
  }

  restoreFirstAvailableConfiguration(
    specialty?: FirstAvailableConfiguration
  ): FirstAvailableConfiguration | undefined {
    if (!specialty) return undefined;
    return awsdk.AWSDKFactory.restoreFirstAvailableConfiguration(JSON.stringify(specialty));
  }

  restoreVisitContext(visitContext?: VisitContext): VisitContext | undefined {
    if (!visitContext) return undefined;
    return awsdk.AWSDKFactory.restoreVisitContext(JSON.stringify(visitContext));
  }

  restoreDisclaimersAcknowledged(disclaimersAcknowledged?: boolean): boolean {
    return disclaimersAcknowledged || false;
  }

  restoreAppointment(appointment?: Appointment): Appointment | undefined {
    if (!appointment) return undefined;
    return awsdk.AWSDKFactory.restoreAppointment(JSON.stringify(appointment));
  }

  restoreVisit(visit?: Visit): Visit | undefined {
    if (!visit) return undefined;
    return awsdk.AWSDKFactory.restoreVisit(JSON.stringify(visit));
  }

  restoreVisitSummary(visitSummary?: VisitSummary): VisitSummary | undefined {
    if (!visitSummary) return undefined;
    return awsdk.AWSDKFactory.restoreVisitSummary(JSON.stringify(visitSummary));
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  /**
   * Method: hydrate
   * @desc Returns amwell object(s) version of static objects
   * @param {Object} data should match Storage interface object
   */
  hydrate(data: types.HydrateData<types.Storage>): types.Storage {
    const result: types.Storage = {};

    this.storageService.keys.forEach(k => {
      if (data[k]) {
        switch (k) {
          case 'consumer':
          case 'activeConsumer':
            result[k] = this.restoreConsumer(data[k]);
            break;
          case 'authentication':
            result[k] = this.restoreAuthentication(data[k]);
            break;
          case 'practice':
            result[k] = this.restorePractice(data[k]);
            break;
          case 'provider':
            result[k] = this.restoreProvider(data[k]);
            break;
          case 'specialty':
            result[k] = this.restoreFirstAvailableConfiguration(data[k]);
            break;
          case 'visitContext':
            result[k] = this.restoreVisitContext(data[k]);
            break;
          case 'appointment':
            result[k] = this.restoreAppointment(data[k]);
            break;
          case 'visit':
            result[k] = this.restoreVisit(data[k]);
            break;
          case 'visitSummary':
            result[k] = this.restoreVisitSummary(data[k]);
            break;
          default:
        }
      }
    });

    return result;
  }

  /**
   * Method: set
   * @desc Sets the awsdk instance object into localStorage.
   * @param {Object} data should match Storage interface
   */
  set(data: types.SetData<types.Storage>): Promise<void> {
    const storage = this.storageService.get() as types.Storage;
    const result: types.Storage = {};

    this.storageService.keys.forEach(k => {
      if (data[k]) {
        result[k] = getData(data[k]);
      } else if (storage[k]) {
        result[k] = getData(storage[k]);
      }
    });

    const resultDuplicate = JSON.stringify(result) === JSON.stringify(storage);
    if (resultDuplicate) return Promise.resolve();

    return this.storageService.set(result);
  }

  /**
   * Method: get
   * @desc Retrieves the awsdk instance object from localStorage.
   */
  get(): types.Storage {
    const storage = this.storageService.get() as types.Storage;

    const {
      instanceState,
      consumer,
      authentication,
      activeConsumer,
      practice,
      provider,
      specialty,
      visitContext,
      disclaimersAcknowledged,
      visit,
      visitSummary,
      appointment
    } = storage;

    return {
      instanceState,
      consumer: this.restoreConsumer(consumer),
      activeConsumer: this.restoreConsumer(activeConsumer),
      authentication: this.restoreAuthentication(authentication),
      practice: this.restorePractice(practice),
      provider: this.restoreProvider(provider),
      specialty: this.restoreFirstAvailableConfiguration(specialty),
      visitContext: this.restoreVisitContext(visitContext),
      appointment: this.restoreAppointment(appointment),
      disclaimersAcknowledged: this.restoreDisclaimersAcknowledged(disclaimersAcknowledged),
      visit: this.restoreVisit(visit),
      visitSummary: this.restoreVisitSummary(visitSummary)
    };
  }

  /**
   * Method: remove
   * @desc Removes the awsdk instance object by key in localStorage.
   * @param {Object} data
   * @param {string[]} data.keys array of keys in localStorage
   */
  remove(data: types.RemoveData): Promise<void> {
    const storage = this.get();

    data.keys.forEach(k => {
      if (this.storageService.hasKey(k)) {
        storage[k] = undefined;
      }
    });

    return this.storageService.set(storage);
  }

  /**
   * Method: clear
   * @desc Clears the awsdk instance object from localStorage.
   * @param {Object} data
   * @param {string} data.opts 'all' | 'session'
   */
  clear(data: types.ClearData = { opts: 'all' }): Promise<void> | void {
    switch (data.opts) {
      case 'session':
        return this.remove({ keys: sessionKeys });
      case 'all':
      default:
        return this.storageService.clear();
    }
  }
}
